/**
 * 29. Fixed Assets — asset register; detail with depreciation schedule;
 * acquisition/disposal/impairment.
 *
 * Arabic and English (C7): every word from the catalogs; statuses agree with
 * the asset.
 */
import {
  Card,
  PageHeader,
  DataTable,
  Skeleton,
  StatusBadge,
} from "@/components/ui";
import { PendingButton } from "@/components/PendingAction";
import { useApiList } from "@/lib/api/hooks";
import { useTranslation } from "@/lib/i18n";
import { listTitle } from "@/lib/i18n/format";
import { formatMoney, formatDate } from "@/lib/money";

interface FixedAssetRow {
  id: string;
  assetNumber: string;
  name: string;
  category: string;
  status: "Draft" | "Active" | "Depreciated" | "Disposed" | "Impairment";
  acquisitionDate: string;
  acquisitionCost: string;
  netBookValue: string;
  currencyCode: string;
}

export function FixedAssetsPage() {
  const { t } = useTranslation();
  const { data, isLoading } = useApiList<FixedAssetRow>(
    ["finance", "fixed-assets"],
    "/finance/fixed-assets",
  );

  return (
    <div>
      <PageHeader
        title={t("finance.assets.title")}
        subtitle={t("finance.assets.subtitle")}
        actions={
          <PendingButton variant="primary" reason="fixedAssets" slice="AE-W4">
            {t("finance.assets.new")}
          </PendingButton>
        }
      />
      {isLoading || !data ? (
        <Skeleton rows={10} />
      ) : (
        <Card title={listTitle(t("finance.assets.list"), data.total)}>
          <DataTable<FixedAssetRow>
            rows={data.items}
            columns={[
              {
                key: "assetNumber",
                label: t("fields.number"),
                render: (r) => (
                  <span className="atlas-mono">{r.assetNumber}</span>
                ),
              },
              { key: "name", label: t("fields.name") },
              { key: "category", label: t("fields.category") },
              {
                key: "acquisitionDate",
                label: t("finance.assets.acquired"),
                render: (r) => formatDate(r.acquisitionDate),
              },
              {
                key: "acquisitionCost",
                label: t("finance.assets.cost"),
                numeric: true,
                render: (r) => formatMoney(r.acquisitionCost, r.currencyCode),
              },
              {
                key: "netBookValue",
                label: t("finance.assets.netBookValue"),
                numeric: true,
                render: (r) => formatMoney(r.netBookValue, r.currencyCode),
              },
              {
                key: "status",
                label: t("fields.status"),
                render: (r) => (
                  <StatusBadge status={r.status} type="FixedAsset" />
                ),
              },
            ]}
          />
        </Card>
      )}
    </div>
  );
}
